import type { Theme, Shadows, Components } from "@mui/material/styles";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { menuItemClasses } from "@mui/material/MenuItem";
import { inputBaseClasses } from "@mui/material/InputBase";
import { CssBaseline, GlobalStyles } from "@mui/material";
import type { ReactNode } from "react";
import { LicenseInfo } from "@mui/x-license";
import { appConfig } from "../appConfig";
import { MuiOutlinedInput } from "./MuiOutlinedInput";
import { MuiButton } from "./MuiButton";
import { MuiAutocomplete } from "./MuiAutocomplete";
import { MuiSelect } from "./MuiSelect";
import { MuiToggleButton } from "./MuiToggleButton";

type Shade = "50" | "100" | "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900";
type ShadeScale = Record<Shade, string>;

type CustomTextColors = { tertiary: string; placeholder: string };
type CustomPalette = {
  red: ShadeScale;
  orange: ShadeScale;
  green: ShadeScale;
  blue: ShadeScale;
  purple: ShadeScale;
  brandBlue: ShadeScale;
  brandGreen: ShadeScale;
  brandOrange: ShadeScale;
  brandRed: ShadeScale;
  teal: ShadeScale;
  ink: ShadeScale;
};

declare module "@mui/material/styles" {
  interface PaletteColor extends ShadeScale {}
  interface SimplePaletteColorOptions extends ShadeScale {}
  interface PaletteGrey extends ShadeScale {}
  interface TypeText extends CustomTextColors {}
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}
}

const baseThemeForUtils = createTheme();

const red: ShadeScale = {
  "900": "#801F30",
  "800": "#A12036",
  "700": "#CC334C",
  "600": "#D44F64",
  "500": "#DB6B7D",
  "400": "#E28594",
  "300": "#EAA1AC",
  "200": "#F1BDC4",
  "100": "#FAD9DE",
  "50": "#FAD9DE",
};
const orange: ShadeScale = {
  "900": "#803B00",
  "800": "#B35300",
  "700": "#C26800",
  "600": "#D17D00",
  "500": "#E5A23E",
  "400": "#EDB45F",
  "300": "#F2C480",
  "200": "#FAD7A2",
  "100": "#FCDFB1",
  "50": "#FCF2DC",
};
const green: ShadeScale = {
  "900": "#254934",
  "800": "#316245",
  "700": "#2D7048",
  "600": "#4E9C54",
  "500": "#67A870",
  "400": "#7FAF7F",
  "300": "#9BC29B",
  "200": "#AFD4AF",
  "100": "#CFE7CF",
  "50": "#EBF7EB",
};
const blue: ShadeScale = {
  "900": "#1B384C",
  "800": "#14476A",
  "700": "#0D5788",
  "600": "#116FAE",
  "500": "#1482CC",
  "400": "#419BD7",
  "300": "#6CB2E1",
  "200": "#99CBEC",
  "100": "#D2EAFA",
  "50": "#E7F2F9",
};

const grey: ShadeScale = {
  "900": "#121212",
  "800": "#292929",
  "700": "#41474A",
  "600": "#787D80",
  "500": "#BAB8B5",
  "400": "#DBD7D1",
  "300": "#F0ECE5",
  "200": "#F6F3EA",
  "100": "#FAF9F4",
  "50": "#FFFFFD",
};

const purple: ShadeScale = {
  "900": "#4A1292",
  "800": "#6618C8",
  "700": "#741BE4",
  "600": "#8A40E8",
  "500": "#964BF6",
  "400": "#A768F8",
  "300": "#C296FA",
  "200": "#D6BDFF",
  "100": "#E4D6FD",
  "50": "#F2EDFD",
};

const teal: ShadeScale = {
  "900": "#2F5144",
  "800": "#3E6B59",
  "700": "#477B67",
  "600": "#549179",
  "500": "#6EB497",
  "400": "#9FD6BE",
  "300": "#B6EDD4",
  "200": "#D2FFEB",
  "100": "#E6FFF3",
  "50": "#F5FFFA",
};

const ink: ShadeScale = {
  "50": "#FFFFFD",
  "100": "#FDFDFC",
  "200": "#F9F9F6",
  "300": "#F5F5F4",
  "400": "#A1A3A5",
  "500": "#868A8E",
  "600": "#6C7174",
  "700": "#565D61",
  "800": "#414649",
  "900": "#292C2E",
};

const brandBlue: ShadeScale = {
  "900": "#253B46",
  "800": "#3C5F71",
  "700": "#477085",
  "600": "#62869D",
  "500": "#7DA4BA",
  "400": "#99BED0",
  "300": "#B8D6E3",
  "200": "#C8E3EF",
  "100": "#DDF0F8",
  "50": "#EEF8FC",
};

const brandGreen: ShadeScale = {
  "900": "#465224",
  "800": "#647533",
  "700": "#798E3E",
  "600": "#8FA749",
  "500": "#A3BA61",
  "400": "#BBD47D",
  "300": "#D1E89A",
  "200": "#E5F5B7",
  "100": "#F1FFC2",
  "50": "#F8FFE1",
};

const brandOrange: ShadeScale = {
  "900": "#65471B",
  "800": "#8D6425",
  "700": "#A5752C",
  "600": "#B98537",
  "500": "#C89446",
  "400": "#E3BE7D",
  "300": "#FFE3B0",
  "200": "#FFECC7",
  "100": "#FFF5E0",
  "50": "#FFF8EB",
};

const brandRed: ShadeScale = {
  "900": "#492C38",
  "800": "#60394A",
  "700": "#75465A",
  "600": "#A4617B",
  "500": "#CE7891",
  "400": "#EC9DAA",
  "300": "#FCD1D1",
  "200": "#FDE2E2",
  "100": "#FEECEC",
  "50": "#FEF5F5",
};

const SHADOW = `0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) `;

const baseTheme = createTheme();
const { pxToRem } = baseThemeForUtils.typography;

const SYSTEM_FONT_STACK = `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`;
const fontFamily = `"FK Grotesk", ${SYSTEM_FONT_STACK}`;

const themeWithoutComponents = createTheme({
  transitions: { create: () => "none" },
  motion: { reducedMotion: "system" },
  shape: { borderRadius: 6 },
  spacing: 6,
  palette: {
    ...baseTheme.palette,
    mode: "light",
    primary: { dark: purple["700"], main: purple["600"], light: purple["500"], contrastText: grey["50"], ...purple },
    secondary: {
      dark: purple["900"],
      main: purple["800"],
      light: purple["700"],
      contrastText: purple["50"],
      ...purple,
    },
    error: { dark: red["900"], main: red["800"], light: red["700"], contrastText: red["50"], ...red },
    warning: { dark: orange["900"], main: orange["800"], light: orange["700"], contrastText: orange["50"], ...orange },
    info: { dark: blue["900"], main: blue["800"], light: blue["700"], contrastText: blue["50"], ...blue },
    success: { dark: green["900"], main: green["800"], light: green["700"], contrastText: green["50"], ...green },
    text: {
      primary: ink["900"],
      secondary: ink["800"],
      tertiary: ink["700"],
      placeholder: ink["400"],
      disabled: ink["400"],
    },
    background: { default: grey["50"], paper: grey["50"] },
    action: {
      disabled: ink["400"],
      hover: grey["100"],
      selected: grey["200"],
      focus: grey["100"],
      disabledBackground: grey["300"],
    },
    divider: grey["300"],
    grey,
    red,
    orange,
    green,
    blue,
    purple,
    brandBlue,
    brandGreen,
    brandOrange,
    brandRed,
    teal,
    ink,
  },
  shadows: ["none", ...Array(24).fill(SHADOW)] as Shadows,
  typography: {
    fontFamily,
    h1: {
      fontFamily: "Ivar Display, serif",
      fontSize: pxToRem(34),
      letterSpacing: "0.6px",
      lineHeight: pxToRem(36),
      fontWeight: 600,
    },
    h2: { fontFamily: "Ivar Display, serif", fontSize: pxToRem(24), letterSpacing: "0.5px", fontWeight: 600 },
    h3: { fontSize: pxToRem(16), letterSpacing: "0.4px", lineHeight: pxToRem(20), fontWeight: 600 },
    h4: { fontSize: pxToRem(16), fontWeight: 600 },
    h5: { fontSize: pxToRem(16), fontWeight: 600 },
    h6: { fontSize: pxToRem(14), fontWeight: 600 },
    caption: { fontSize: pxToRem(14) },
    subtitle1: { fontSize: pxToRem(16) },
    subtitle2: { fontSize: pxToRem(14) },
    body1: { fontSize: pxToRem(16), lineHeight: "1.5" },
    body2: { fontSize: pxToRem(14), lineHeight: "1.5" },
    button: { fontSize: pxToRem(14), textTransform: "none" },
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
  },
});

/** Remember to update the preloading in index.html when changing the font version */
const FONT_VERSION = "7";
const FONT_PATH = `/fonts/v${FONT_VERSION}`;

const DIALOG_PADDING_INLINE = (theme: Theme) => ({
  paddingInline: theme.spacing(6),
  [theme.breakpoints.down("sm")]: { paddingInline: theme.spacing(3) },
});

/** Theme components(https://mui.com/material-ui/customization/theme-components/) */
const themeComponents: Components<Theme> = {
  MuiButtonBase: { defaultProps: { disableRipple: true, disableTouchRipple: true } },
  MuiButton,
  MuiRadio: { defaultProps: { disableRipple: true } },
  MuiCheckbox: { defaultProps: { disableRipple: true }, styleOverrides: { root: { borderRadius: 1 } } },
  MuiTypography: { defaultProps: { color: "text.primary" } },
  MuiOutlinedInput,
  MuiAutocomplete,
  MuiSelect,
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        "color": theme.palette.text.primary,
        "borderRadius": theme.shape.borderRadius,

        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
      }),
    },
  },
  /**
   * Keep 1Password out of our inputs; its inline menu steals ArrowDown from Autocomplete and Select.
   * `MuiTextField` needs its own entry because TextField always passes `inputProps` down, which
   * would override the `MuiInputBase` default rather than merge with it.
   */
  MuiInputBase: {
    defaultProps: { inputProps: { "data-1p-ignore": "true" } },
    styleOverrides: {
      root: ({ theme }) => ({
        /** A readonly field is a disabled input, but its value should still read as normal text. */
        [`&.${inputBaseClasses.disabled}`]: { color: theme.palette.text.primary },
      }),
      input: ({ theme }) => ({
        "&::placeholder": {
          color: theme.palette.text.placeholder,
          WebkitTextFillColor: theme.palette.text.placeholder,
          opacity: 1,
        },
        /**
         * MUI greys out a disabled input with `-webkit-text-fill-color`, which beats `color`, so
         * the rule above cannot undo it on its own. Reset the fill to its initial value rather
         * than naming a colour here: `currentColor` resolves per element, so the input paints the
         * ink the rule above gives it and anything rendered inside it — a placeholder, a chip, a
         * status label — keeps its own.
         */
        [`&.${inputBaseClasses.disabled}`]: { WebkitTextFillColor: "currentColor" },
      }),
    },
  },
  MuiTextField: {
    defaultProps: { variant: "outlined", slotProps: { htmlInput: { "data-1p-ignore": "true" } } },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({ borderColor: theme.palette.divider, backgroundColor: "transparent", height: 0 }),
    },
  },

  /** Dialog */
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        minWidth: "28rem",
        paddingBlock: theme.spacing(6),
        [theme.breakpoints.down("sm")]: { minWidth: "98%", paddingBlock: theme.spacing(4), margin: 1 },
        borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
      }),
    },
  },
  MuiDialogTitle: {
    defaultProps: { variant: "h2" },
    styleOverrides: {
      root: ({ theme }) => ({
        paddingTop: 0,
        paddingBottom: theme.spacing(3),
        ...DIALOG_PADDING_INLINE(theme),
        fontSize: theme.typography.pxToRem(24),
        fontWeight: 600,
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({ paddingBlock: 0, ...DIALOG_PADDING_INLINE(theme), marginTop: theme.spacing(2) }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        overflow: "auto",
        paddingTop: `${theme.spacing(1)} !important`,
        paddingBottom: `${theme.spacing(4)} !important`,
        ...DIALOG_PADDING_INLINE(theme),
      }),
    },
  },

  MuiToggleButton,
  /** Menu */
  MuiMenu: {
    defaultProps: { transitionDuration: 0, elevation: 0 },
    styleOverrides: {
      list: { padding: 0, minWidth: "13rem" },
      paper: ({ theme: muiTheme }) => ({
        border: `1px solid ${muiTheme.palette.grey["300"]}`,
        borderRadius: muiTheme.shape.borderRadius,
        marginTop: muiTheme.spacing(0.5),
        boxShadow: muiTheme.shadows[1],
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        "margin": theme.spacing(0.5),
        "borderRadius": `calc(${theme.shape.borderRadius}px * 0.5)`,
        "paddingInline": theme.spacing(2),
        "paddingBlock": theme.spacing(1.5),
        "&&:hover": { background: theme.palette.action.hover },
        "&&:focus-visible": { background: theme.palette.action.focus },
        [`&&.${menuItemClasses.selected}`]: { background: theme.palette.action.selected },
        "fontSize": theme.typography.pxToRem(14),
        "display": "flex",
        "gap": theme.spacing(1.5),
      }),
    },
  },

  MuiCssBaseline: {
    styleOverrides: `
    @font-face {
        font-family: 'FK Grotesk';
        font-style: normal;
        font-display: swap;
        font-weight: 400; 
        src: url('${FONT_PATH}/FKGrotesk-Regular.woff2') format('woff2');
      }
      @font-face {
        font-family: 'FK Grotesk';
        font-style: normal;
        font-display: swap;
        font-weight: 600;
        src: url('${FONT_PATH}/FKGrotesk-Medium.woff2') format('woff2');
      }
      @font-face {
        font-family: 'FK Grotesk';
        font-style: normal;
        font-display: swap;
        font-weight: 700;
        src: url('${FONT_PATH}/FKGrotesk-Bold.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Ivar Display';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
        src: url('${FONT_PATH}/IvarDisplay-Regular.woff2') format('woff2');
      }
    `,
  },
};

LicenseInfo.setLicenseKey(appConfig.MUI_X_LICENSE_KEY);

const theme = createTheme(themeWithoutComponents, { components: themeComponents });
const globalStyles = <GlobalStyles styles={{ html: { overscrollBehavior: "none", fontFamily } }} />;

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      {children}
    </MuiThemeProvider>
  );
};
